import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Avatar,
    Paper,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Tooltip,
    Fab
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    People as PeopleIcon,
    Share as ShareIcon,
    Settings as SettingsIcon,
    ViewList as ListViewIcon,
    ViewKanban as KanbanViewIcon,
    CalendarMonth as CalendarViewIcon,
    ArrowBack as BackIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import TaskDetailPanel from '../components/TaskDetailPanel';
import { useCatAssistant } from '../contexts/CatAssistantContext';

interface Task {
    id: number;
    title: string;
    description: string;
    status: 'todo' | 'in_progress' | 'blocked' | 'done';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    position: number;
    assignedTo?: {
        id: number;
        name: string;
        email: string;
    };
    createdBy: {
        id: number;
        name: string;
        email: string;
    };
    startDate?: string;
    dueDate?: string;
    labels: string[];
    subtasks: Subtask[];
    reservation?: {
        id: number;
        customerName: string;
        startDate: string;
        endDate: string;
    };
    client?: {
        id: number;
        name: string;
        email: string;
    };
    estimatedHours?: number;
    actualHours?: number;
    attachments?: any[];
    comments?: any[];
}

interface Subtask {
    id: number;
    title: string;
    isCompleted: boolean;
    completedBy?: {
        id: number;
        name: string;
    };
    completedAt?: string;
}

interface Project {
    id: number;
    name: string;
    description: string;
    color: string;
    icon: string;
    owner: {
        id: number;
        name: string;
        email: string;
    };
    members: ProjectMember[];
    role: 'owner' | 'admin' | 'collaborator' | 'viewer' | 'client';
    settings: {
        kanbanColumns: KanbanColumn[];
        defaultView: 'kanban' | 'list' | 'calendar';
    };
}

interface ProjectMember {
    id: number;
    role: string;
    user: {
        id: number;
        name: string;
        email: string;
    };
}

interface KanbanColumn {
    id: string;
    name: string;
    color: string;
}

interface CreateTaskData {
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    assignedTo?: number;
    dueDate?: string;
    labels: string[];
}

const ProjectDetail: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { taskMoved, taskCompleted, motivate } = useCatAssistant();

    const [project, setProject] = useState<Project | null>(null);
    const [tasks, setTasks] = useState<{
        todo: Task[];
        in_progress: Task[];
        blocked: Task[];
        done: Task[];
    }>({ todo: [], in_progress: [], blocked: [], done: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentView, setCurrentView] = useState<'kanban' | 'list' | 'calendar'>('kanban');
    const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedColumn, setSelectedColumn] = useState<string>('');
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
    const [taskDetailPanelOpen, setTaskDetailPanelOpen] = useState(false);

    const [newTask, setNewTask] = useState<CreateTaskData>({
        title: '',
        description: '',
        priority: 'medium',
        labels: []
    });

    const priorityColors = {
        low: '#10B981',
        medium: '#3B82F6',
        high: '#F59E0B',
        urgent: '#EF4444'
    };

    useEffect(() => {
        if (projectId) {
            fetchProjectData();
        }
    }, [projectId]);

    const fetchProjectData = async () => {
        try {
            setLoading(true);
            const [projectResponse, tasksResponse] = await Promise.all([
                axios.get(`/api/tasks/projects/${projectId}`),
                axios.get(`/api/tasks/projects/${projectId}/tasks`)
            ]);

            setProject(projectResponse.data);
            setTasks(tasksResponse.data);
            setError(null);
        } catch (err: any) {
            console.error('Error fetching project data:', err);
            setError(err.response?.data?.error || 'Failed to load project data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTask = async () => {
        if (!newTask.title.trim() || !selectedColumn) {
            setError('Task title and column are required');
            return;
        }

        try {
            const response = await axios.post(`/api/tasks/projects/${projectId}/tasks`, {
                ...newTask,
                status: selectedColumn
            });

            // Add task to the appropriate column
            setTasks(prev => ({
                ...prev,
                [selectedColumn]: [...prev[selectedColumn as keyof typeof prev], response.data]
            }));

            setCreateTaskDialogOpen(false);
            setNewTask({
                title: '',
                description: '',
                priority: 'medium',
                labels: []
            });
            setSelectedColumn('');
            setError(null);
        } catch (err: any) {
            console.error('Error creating task:', err);
            setError(err.response?.data?.error || 'Failed to create task');
        }
    };

    const handleDragEnd = async (result: any) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const sourceColumn = source.droppableId;
        const destColumn = destination.droppableId;
        const taskId = parseInt(draggableId);

        // Get task title for cat message
        const movedTaskTitle = tasks[sourceColumn as keyof typeof tasks]?.[source.index]?.title || 'Tarea';

        // Update UI immediately
        setTasks(prev => {
            const newTasks = { ...prev };
            const sourceTasks = Array.from(newTasks[sourceColumn as keyof typeof newTasks]);
            const [movedTask] = sourceTasks.splice(source.index, 1);

            const destTasks = Array.from(newTasks[destColumn as keyof typeof newTasks]);
            destTasks.splice(destination.index, 0, { ...movedTask, status: destColumn });

            return {
                ...newTasks,
                [sourceColumn]: sourceTasks,
                [destColumn]: destTasks
            };
        });

        try {
            await axios.put(`/api/tasks/${taskId}/move`, {
                status: destColumn,
                position: destination.index
            });

            // 🐱 Cat reactions!
            if (destColumn === 'done') {
                // Epic celebration for completed task!
                taskCompleted(movedTaskTitle);
            } else {
                // Regular move notification
                taskMoved(movedTaskTitle, destColumn);
            }
        } catch (err: any) {
            console.error('Error moving task:', err);
            // Revert UI on error
            fetchProjectData();
        }
    };

    const openCreateTaskDialog = (columnId: string) => {
        setSelectedColumn(columnId);
        setCreateTaskDialogOpen(true);
    };

    const handleTaskClick = (taskId: number) => {
        setSelectedTaskId(taskId);
        setTaskDetailPanelOpen(true);
    };

    const handleTaskUpdate = (updatedTask: Task) => {
        // Update the task in the local state
        setTasks(prev => ({
            ...prev,
            [updatedTask.status]: prev[updatedTask.status as keyof typeof prev].map(task =>
                task.id === updatedTask.id ? updatedTask : task
            )
        }));
    };

    const handleTaskDetailPanelClose = () => {
        setTaskDetailPanelOpen(false);
        setSelectedTaskId(null);
    };

    const getTasksByPriority = (tasks: Task[], priority: string) => {
        return tasks.filter(task => task.priority === priority);
    };

    const getPriorityColor = (priority: string) => {
        return priorityColors[priority as keyof typeof priorityColors] || '#6B7280';
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error && !project) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    {error}
                    <Button onClick={() => navigate('/tasks')} sx={{ ml: 2 }}>
                        Volver
                    </Button>
                </Alert>
            </Container>
        );
    }

    if (!project) return null;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/tasks')} sx={{ mr: 2 }}>
                        <BackIcon />
                    </IconButton>
                    <Avatar
                        sx={{
                            bgcolor: project.color,
                            width: 48,
                            height: 48,
                            fontSize: '1.5rem',
                            mr: 2
                        }}
                    >
                        {project.icon}
                    </Avatar>
                    <Box>
                        <Typography variant="h4" component="h1">
                            {project.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {project.description}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            {project.members.length} miembros
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openCreateTaskDialog('todo')}
                    >
                        Nueva Tarea
                    </Button>
                    <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                        <MoreVertIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={currentView}
                    onChange={(e, newValue) => setCurrentView(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<KanbanViewIcon />} label="Kanban" value="kanban" />
                    <Tab icon={<ListViewIcon />} label="Lista" value="list" />
                    <Tab icon={<CalendarViewIcon />} label="Calendario" value="calendar" />
                </Tabs>
            </Paper>

            {currentView === 'kanban' && project && Object.keys(tasks).length > 0 && (
                <DragDropContext key={project.id} onDragEnd={handleDragEnd}>
                    <Box sx={{ display: 'flex', gap: 3, overflowX: 'auto', pb: 2 }}>
                        {project.settings?.kanbanColumns?.map((column) => (
                            <Paper
                                key={column.id}
                                sx={{
                                    minWidth: 300,
                                    maxWidth: 300,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    p: 2
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                bgcolor: column.color,
                                                borderRadius: '50%'
                                            }}
                                        />
                                        <Typography variant="h6">
                                            {column.name}
                                        </Typography>
                                        <Chip
                                            label={tasks[column.id as keyof typeof tasks]?.length || 0}
                                            size="small"
                                            sx={{ bgcolor: column.color, color: 'white' }}
                                        />
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => openCreateTaskDialog(column.id)}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>

                                <Droppable droppableId={column.id}>
                                    {(provided, snapshot) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            sx={{
                                                minHeight: 100,
                                                bgcolor: snapshot.isDraggingOver ? 'grey.100' : 'transparent',
                                                borderRadius: 1,
                                                transition: 'background-color 0.2s'
                                            }}
                                        >
                                            {tasks[column.id as keyof typeof tasks]?.map((task, index) => (
                                                <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                                                    {(provided, snapshot) => (
                                                        <Paper
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            onClick={() => handleTaskClick(task.id)}
                                                            sx={{
                                                                p: 2,
                                                                mb: 1,
                                                                cursor: 'pointer',
                                                                borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
                                                                opacity: snapshot.isDragging ? 0.8 : 1,
                                                                transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    boxShadow: 2
                                                                }
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                {task.title}
                                                            </Typography>

                                                            {task.description && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                    {task.description.substring(0, 100)}
                                                                    {task.description.length > 100 && '...'}
                                                                </Typography>
                                                            )}

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    <Chip
                                                                        label={task.priority}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: getPriorityColor(task.priority),
                                                                            color: 'white',
                                                                            textTransform: 'capitalize'
                                                                        }}
                                                                    />
                                                                    {(Array.isArray(task.labels) ? task.labels : []).map((label) => (
                                                                        <Chip
                                                                            key={label}
                                                                            label={label}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                </Box>

                                                                {task.assignedTo?.name && (
                                                                    <Tooltip title={task.assignedTo.name}>
                                                                        <Avatar
                                                                            sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                                                                        >
                                                                            {task.assignedTo.name.charAt(0)}
                                                                        </Avatar>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>

                                                            {task.dueDate && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                                    Vence: {format(new Date(task.dueDate), 'dd MMM yyyy', { locale: es })}
                                                                </Typography>
                                                            )}

                                                            {task.reservation && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Chip
                                                                        label={`Reserva #${task.reservation.id}`}
                                                                        size="small"
                                                                        color="primary"
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Paper>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                            </Paper>
                        ))}
                    </Box>
                </DragDropContext>
            )}

            {currentView === 'list' && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Vista de Lista - Próximamente
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Esta funcionalidad estará disponible en la siguiente actualización.
                    </Typography>
                </Paper>
            )}

            {currentView === 'calendar' && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Vista de Calendario - Próximamente
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Esta funcionalidad estará disponible en la siguiente actualización.
                    </Typography>
                </Paper>
            )}

            {/* Project Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
            >
                <MenuItem onClick={() => {
                    navigate(`/tasks/project/${projectId}/members`);
                    setMenuAnchor(null);
                }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    Gestionar Miembros
                </MenuItem>
                <MenuItem onClick={() => {
                    // TODO: Implement share dialog
                    setMenuAnchor(null);
                }}>
                    <ShareIcon sx={{ mr: 1 }} />
                    Compartir Proyecto
                </MenuItem>
                <MenuItem onClick={() => {
                    navigate(`/tasks/project/${projectId}/settings`);
                    setMenuAnchor(null);
                }}>
                    <SettingsIcon sx={{ mr: 1 }} />
                    Configuración
                </MenuItem>
            </Menu>

            {/* Create Task Dialog */}
            <Dialog open={createTaskDialogOpen} onClose={() => setCreateTaskDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Crear Nueva Tarea</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Título de la tarea"
                            value={newTask.title}
                            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Descripción"
                            value={newTask.description}
                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Prioridad</InputLabel>
                            <Select
                                value={newTask.priority}
                                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                            >
                                <MenuItem value="low">Baja</MenuItem>
                                <MenuItem value="medium">Media</MenuItem>
                                <MenuItem value="high">Alta</MenuItem>
                                <MenuItem value="urgent">Urgente</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Fecha de vencimiento"
                            type="date"
                            value={newTask.dueDate || ''}
                            onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateTaskDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button onClick={handleCreateTask} variant="contained">
                        Crear Tarea
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Task Detail Panel */}
            <TaskDetailPanel
                taskId={selectedTaskId}
                open={taskDetailPanelOpen}
                onClose={handleTaskDetailPanelClose}
                onUpdate={handleTaskUpdate}
            />
        </Container>
    );
};

export default ProjectDetail;